<template>
  <div class="router-transitions container-fluid flex-grow-1 container-p-y">
    <h4 v-if="activeStep !== 0" class="d-flex justify-content-between align-items-center w-100 font-weight-bold py-3 mb-4">
      Employees Import
    </h4>
    <div v-if="floors && floors.length" class="col">
      <div class="text-center py-5" v-if="activeStep === 0">
        <EmployeesImportStats @create="setActiveStep(1)" :booking-enabled="user.booking_enabled" />
      </div>
      <ul class="nav nav-lg nav-tabs tabs-alt" v-if="activeStep > 0">
        <li class="nav-item" @click="setActiveStep(1)">
          <a :class="['nav-link', { active: activeStep === 1 }]" href="#">
            <i class="lnr lnr-checkmark-circle d-block mr-2" v-if="file"></i>
            <span class="mr-2" v-else>1. </span>
            Select File
          </a>
        </li>
        <li class="nav-item" @click="setActiveStep(2)">
          <a :class="['nav-link', { active: activeStep === 2 }]" href="#">
            <i class="lnr lnr-checkmark-circle d-block mr-2" v-if="isColumnsConfirmed"></i>
            <span class="mr-2" v-else>2. </span>
            Map Columns
          </a>
        </li>
        <li class="nav-item" @click="isColumnsConfirmed && setActiveStep(3)">
          <a :class="['nav-link', { active: activeStep === 3 }]" href="#">
            <span class="mr-2">3. </span>
            Upload Employees
          </a>
        </li>
      </ul>
      <div class="dnd-wrapper" v-if="activeStep === 1">
        <div class="dnd">
          <app-files-selector
            @change="onFileSelect"
            accept-input=".csv"
            mime-filter="application/vnd.ms-excel,text/csv"
            :drop-area="document"
            >Drop CSV file here or click to select and start employee import...
          </app-files-selector>
        </div>
      </div>
      <b-card no-body v-if="activeStep === 2 && file">
        <div class="table-responsive">
          <div class="d-flex align-items-center">
            <b-form-group class="first-row-toggle m-2">
              <b-form-checkbox v-model="skipFirstRow">First Row is Columns Names</b-form-checkbox>
              <b-form-checkbox v-if="isDeskColumnsSelected && user.booking_enabled" v-model="releaseDesks">
                Release Desks if Busy
              </b-form-checkbox>
              <b-form-checkbox v-model="createUsers">Create User Accounts if not Exist</b-form-checkbox>
              <b-form-checkbox v-model="deleteNotListedEmployees">Delete Employees not in import file</b-form-checkbox>
              <b-form-checkbox v-model="deleteNotListedUsers">Delete Users not in import file</b-form-checkbox>
              <b-form-checkbox v-if="user.booking_enabled" v-model="createInfinityEventForced">Infinity Desk Booking</b-form-checkbox>
            </b-form-group>
            <div class="ml-auto mt-auto mr-4 mb-2 text-right">
              <div class="text-danger" v-if="columnsBinding.name === null">
                Column `Name` required
              </div>
              <div class="text-danger" v-if="columnsBinding.email === null">
                Column `Email` required
              </div>
              <div class="text-danger" v-if="deleteNotListedEmployees">
                All Existing Employees not listed in file will be deleted
              </div>
              <div class="text-danger" v-if="deleteNotListedUsers">
                All Existing Users not listed in file will be deleted
              </div>
            </div>
          </div>
          <table class="table">
            <thead>
              <tr>
                <th v-for="(_, i) in parsedCsv.data[0]" :key="i">
                  <EmployeeColumnSelect :num="i + 1" :columns-binding="columnsBinding" class="col" @change="changeColumnBinding" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in bindingRandomRows" :key="`row-${i}`">
                <td v-for="(cell, j) in row" :key="`cell-${j}`">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
          <b-form-group class="m-2" v-if="isConfigStepValid">
            <b-button type="button" variant="primary" @click.prevent="completeStep2">Confirm & Go to final step</b-button>
          </b-form-group>
        </div>
      </b-card>
      <b-card no-body v-if="activeStep === 3 && isColumnsConfirmed">
        <div class="table-responsive">
          <b-button type="button" variant="primary" class="ml-auto" @click.prevent="startImport">Start Import</b-button>
          <EmployeeImportFinalTable :items="importEmployees" :fields="fields" />
          <b-form-group class="m-2 text-right">
            <b-button type="button" variant="primary" @click.prevent="startImport">Start Import</b-button>
          </b-form-group>
        </div>
      </b-card>
    </div>
    <b-card v-else class="text-center">
      <h4>
        <router-link :to="{ name: 'floors' }">Create</router-link>
        at least one floor first
      </h4>
    </b-card>
  </div>
</template>

<script>
import Papaparse from 'papaparse'
import { mapActions, mapGetters, mapState } from 'vuex'
import AppFilesSelector from '@/components/shared/AppFilesSelector'
import EmployeeColumnSelect from '@/components/employee/EmployeeColumnSelect'
import { Api } from '@/utils/Api'
import EmployeesImportStats from '@/components/employee-import/EmployeesImportStats'
import EmployeeImportFinalTable from '@/components/employee-import/EmployeeImportFinalTable'
import UrlStore from '@/utils/UrlStore'
import Id from '@/utils/Id'

const employeesImportApi = new Api(UrlStore.EMPLOYEE_IMPORTS)
function getDefaultColumnsBindings() {
  return {
    email: null,
    name: null,
    phone: null,
    desk_number: null,
    cell: null,
    building: null,
    floor: null,
    department: null,
    position: null
  }
}
export default {
  name: 'EmployeesImport',
  components: {
    EmployeeImportFinalTable,
    EmployeesImportStats,
    EmployeeColumnSelect,
    AppFilesSelector
  },
  data: () => ({
    file: null,
    activeStep: 0,
    importEmployees: [],
    document: null,
    isColumnsConfirmed: false,
    parsedCsv: {},
    skipFirstRow: true,
    createUsers: false,
    deleteNotListedEmployees: false,
    deleteNotListedUsers: false,
    createInfinityEventForced: true,
    releaseDesks: false,
    columnsBinding: getDefaultColumnsBindings()
  }),
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('point', ['getPoint']),
    ...mapGetters('building', ['getBuilding']),
    ...mapGetters('floor', ['floors', 'getFloor', 'floorsPublished']),
    ...mapGetters('employee', ['employees', 'getEmployee']),
    fields() {
      return [
        { key: 'num', label: '#' },
        { key: 'name' },
        { key: 'email' },
        { key: 'phone' },
        { key: 'cell', label: 'Cell Phone' },
        { key: 'department' },
        { key: 'position' },
        { key: 'building' },
        { key: 'floor' },
        { key: 'desk_number' },
        { key: 'actions', label: '' }
      ]
    },
    isDeskColumnsSelected() {
      return !!this.columnsBinding.desk_number
    },
    isConfigStepValid() {
      let valid = this.columnsBinding.name !== 0
      if (this.createUsers) valid = this.columnsBinding.email !== 0
      return valid
    },
    bindingRandomRows() {
      const rowsCount = this.parsedCsv.data.length
      if (rowsCount) {
        if (rowsCount > 9) {
          const rowNums = new Set([0])
          while (rowNums.size < 9) {
            rowNums.add(Math.floor(rowsCount * Math.random()))
          }
          return [...rowNums].map((rowNum) => {
            return this.parsedCsv.data[rowNum]
          })
        } else {
          return this.parsedCsv.data
        }
      }
      return []
    }
  },
  methods: {
    ...mapActions('floor', ['floorsLoad']),
    setActiveStep(stepNumber) {
      let isValid = false

      switch (stepNumber) {
        case 0:
        case 1:
          this.columnsBinding = getDefaultColumnsBindings()
          this.file = null
          this.importEmployees = []
          this.isColumnsConfirmed = false
          isValid = true
          break
        case 2:
          this.isColumnsConfirmed = false
          isValid = Boolean(this.file)
          break
        case 3:
          isValid = this.isColumnsConfirmed
          break
      }
      if (isValid) this.activeStep = stepNumber
    },
    async startImport() {
      await employeesImportApi.save({
        id: Id(),
        items: this.importEmployees,
        delete_not_listed_employees: this.deleteNotListedEmployees,
        delete_not_listed_users: this.deleteNotListedUsers,
        create_infinity_event_forced: this.createInfinityEventForced,
        create_users: this.createUsers,
        release_desks: this.releaseDesks || !this.user.booking_enabled
      })
      this.setActiveStep(0)
    },
    changeColumnBinding({ num, fieldName }) {
      if (fieldName) {
        this.columnsBinding[fieldName] = num
      } else {
        Object.keys(this.columnsBinding).forEach((name) => {
          if (this.columnsBinding[name] === num) {
            this.columnsBinding[name] = null
          }
        })
      }
    },
    completeStep2() {
      this.generateImportEmployees()
      this.setActiveStep(3)
    },
    generateImportEmployees() {
      const fields = Object.keys(this.columnsBinding)
      const importEmployees = []
      const dataLen = this.parsedCsv.data.length
      for (let i = 0; dataLen > i; i++) {
        if (i === 0 && this.skipFirstRow) continue
        const row = this.parsedCsv.data[i]
        const employee = {}
        fields.forEach((fieldName) => {
          let fieldNum = this.columnsBinding[fieldName]
          if (fieldNum) {
            employee[fieldName] = row[fieldNum - 1]
          }
        })
        importEmployees.push(employee)
      }

      this.importEmployees = importEmployees
      this.isColumnsConfirmed = true
    },
    onParseComplete(data) {
      if (!data.errors.length) {
        this.parsedCsv = data
        this.setActiveStep(2)
      } else {
        console.error(data.errors)
        this.$appToast.error("Can't parse passed CSV file. Please contact with support.")
      }
    },
    parseCsv(file) {
      if (file) {
        this.importEmployees = []
        Papaparse.parse(file, {
          complete: this.onParseComplete.bind(this),
          skipEmptyLines: true
        })
      } else {
        this.$appToast.error("Didn't get any file for parse. Please contact with support.")
      }
    },
    onFileSelect(files) {
      this.file = files[0]
    }
  },
  created() {
    this.document = document.documentElement
    if (!this.floors || this.floors.length === 0) {
      this.floorsLoad()
    }
  },
  watch: {
    file: {
      handler(file) {
        file && this.parseCsv(file)
      }
    },
    isDeskColumnsSelected(v) {
      if (!v) this.releaseDesks = false
    }
  }
}
</script>

<style scoped lang="scss">
.user-name {
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 2.5rem;
  height: 2.5rem;
  margin-right: 1rem;
}

.first-row-toggle {
  .custom-checkbox {
    padding-left: 1.7rem;
  }
}

.table-responsive {
  padding: 1rem;
}
</style>

<style>
/*.employee-import-content {*/
/*  display: flex;*/
/*  justify-content: center;*/
/*}*/

.nav-link {
  display: flex;
  align-items: center;
  justify-content: center;
}

.employee-table td {
  vertical-align: middle;
}

.dnd-wrapper {
  background: #fff;
  padding: 2rem;
}

.dnd {
  padding: 5rem 3rem;
  font-size: 1.3rem;
  text-align: center;
  border: 3px dashed rgba(24, 28, 33, 0.06);
}
</style>
